import { useParams } from '@tanstack/react-router';
import { Printer } from 'lucide-react';
import { useEffect } from 'react';
import { useTrip } from '../app/data.ts';
import { cx } from '../kit/cx.ts';
import { Button, DateFlag } from '../kit/index.ts';
import t from '../kit/type.module.css';
import { flag, time } from '../model/trip.ts';
import { duration, instant, zoneLabel } from '../model/zones.ts';
import s from './Print.module.css';

const WEEKDAY = new Intl.DateTimeFormat('en-GB', { weekday: 'long', timeZone: 'UTC' });
const JOURNEY = new Set(['flight', 'train', 'bus', 'ferry']);

/** The pre-trip document (F-13 FR-EXP-04), as the apps' PDF: cover, bookings, a page per day with
 *  local-script names and addresses for taxi drivers, then notes and lists. Printed by the browser. */
export function Print() {
  const { tripId } = useParams({ from: '/app/trips/$tripId/print' });
  const { model, error } = useTrip(tripId);
  // Paper is light whatever the screen's theme: the tokens' light set while this page is open.
  useEffect(() => {
    const root = document.documentElement;
    const before = root.dataset.theme;
    root.dataset.theme = 'light';
    return () => {
      if (before) root.dataset.theme = before;
      else delete root.dataset.theme;
    };
  }, []);
  if (error) return <p role="alert">{error.message}</p>;
  if (!model) return <p className={t.bodyS}>Loading…</p>;
  const title = (itemId: string | null) => {
    for (const p of [...model.days.flatMap((d) => d.items), ...model.lists.flatMap((l) => l.items)])
      if (p.item.id === itemId) return p.item.title;
    return 'Booking';
  };
  return (
    <div className={s.doc}>
      <div className={s.bar}>
        <span className={cx(t.bodyS, s.muted)}>
          A4, one day per page. Choose "Save as PDF" to keep a copy offline.
        </span>
        <Button variant="primary" icon={Printer} onClick={() => window.print()}>
          Print
        </Button>
      </div>

      <section className={cx(s.page, s.cover)}>
        <div className={cx(t.label, s.muted)}>Pathfinder · pre-trip</div>
        <h1 className={cx(t.displayXL, s.flat)}>{model.title}</h1>
        <div className={t.readoutL}>
          {flag(model.startDate)} ▶ {flag(model.endDate)}
        </div>
        <ul className={s.plain}>
          {model.destinations.map((d) => (
            <li key={d.id} className={t.body}>
              {`// ${d.name}`}{' '}
              <span className={cx(t.readoutS, s.muted)}>
                {flag(d.startDate)}–{flag(d.endDate)} · {d.localCurrency ?? ''} · {zoneLabel(d.tz, null)}
              </span>
            </li>
          ))}
        </ul>
      </section>

      {model.reservations.length > 0 && (
        <section className={s.page}>
          <h2 className={cx(t.displayL, s.flat)}>Bookings</h2>
          <table className={s.table}>
            <tbody>
              {model.reservations.map((r) => {
                const a = instant(r.startLocal, r.startTz);
                const b = instant(r.endLocal, r.endTz ?? r.startTz);
                const d = r.details;
                return (
                  <tr key={r.id}>
                    <td className={t.label}>{r.kind}</td>
                    <td>
                      <div className={t.titleM}>{title(r.itemId)}</div>
                      <div className={t.caption}>
                        {JOURNEY.has(r.kind) && d.from && d.to ? `${d.from} → ${d.to} · ` : ''}
                        {flag(r.startLocal)} {time(r.startLocal) ?? ''} {zoneLabel(r.startTz, a)}
                        {r.endLocal
                          ? ` → ${flag(r.endLocal)} ${time(r.endLocal) ?? ''} ${zoneLabel(r.endTz ?? r.startTz, b)}`
                          : ''}
                        {JOURNEY.has(r.kind) && a !== null && b !== null && b > a
                          ? ` · ${duration(Math.round((b - a) / 60000))}`
                          : ''}
                      </div>
                      {d.address && <div className={t.caption}>{d.address}</div>}
                    </td>
                    <td className={cx(t.readoutM, s.code)}>{r.confirmation ?? ''}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </section>
      )}

      {model.days.map((d) => (
        <section key={d.id} className={s.page}>
          <div className={s.dayHead}>
            <DateFlag text={flag(d.date)} />
            <h2 className={cx(t.displayS, s.flat)}>
              Day {String(d.number).padStart(2, '0')} / {String(d.count).padStart(2, '0')}
            </h2>
            <span className={cx(t.label, s.muted)}>
              {WEEKDAY.format(new Date(`${d.date}T00:00:00Z`))}
              {d.destination ? ` · // ${d.destination.name}` : ''}
            </span>
          </div>
          {d.title && <p className={cx(t.body, s.flat)}>{d.title}</p>}
          <table className={s.table}>
            <tbody>
              {d.items
                .flatMap((p) => [p, ...p.subItems])
                .map((p) => (
                  <tr key={p.item.id}>
                    <td className={t.readoutS}>{time(p.item.startLocal) ?? ''}</td>
                    <td className={t.readoutS}>{p.stopId}</td>
                    <td>
                      <div className={t.titleM}>
                        {p.item.title}{' '}
                        {p.item.place?.localName && (
                          <span className={cx(t.body, s.local)}>{p.item.place.localName}</span>
                        )}
                      </div>
                      {p.item.place?.address && <div className={t.caption}>{p.item.place.address}</div>}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
          {d.items.length === 0 && <p className={cx(t.bodyS, s.muted)}>Nothing planned.</p>}
        </section>
      ))}

      {(model.notes.some((n) => n.targetType === 'trip') || model.checklists.length > 0) && (
        <section className={s.page}>
          <h2 className={cx(t.displayL, s.flat)}>Info</h2>
          {model.notes
            .filter((n) => n.targetType === 'trip')
            .map((n) => (
              <div key={n.id} className={s.note}>
                {n.title && <h3 className={cx(t.titleM, s.flat)}>{n.title}</h3>}
                <p className={cx(t.bodyS, s.pre)}>
                  {n.body
                    .replace(/\*\*|~~|\*/g, '')
                    .replace(/- \[ \] /g, '☐ ')
                    .replace(/- \[x\] /gi, '☑ ')}
                </p>
              </div>
            ))}
          {model.checklists.map((c) => (
            <div key={c.id} className={s.note}>
              <h3 className={cx(t.titleM, s.flat)}>{c.title}</h3>
              {c.entries.map((e) => (
                <div key={e.id} className={t.bodyS}>{`${e.checked ? '☑' : '☐'} ${e.text}`}</div>
              ))}
            </div>
          ))}
        </section>
      )}
    </div>
  );
}
